import React, { Dispatch } from "react";
import { connect } from "react-redux";
import { showSnack } from "react-redux-snackbar";
import { Icon } from "@cadmakers-ui/icons";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from "reactstrap";
import { Workflow } from "../../Models/Workflow";
import { ApiRequest, appPaths } from "../../../Common";
import { endpoints } from "../..";
import WorkflowActionButton from "./WorkflowActionButton";
import ResponseHandler from "../../../App/ResponseHandler";
import iconStyles from "../../../Common/Styles/Icons.module.scss";

type Props = {
  workflow: Workflow;
  onDelete: (code: string) => void;
};

type DispatchProps = {
  showSnack: (uniqueId: any, args: any) => void;
};

type State = {
  isConfirmDeleteModalOpen: boolean;
};

const mapDispatchToProps = (dispatch: Dispatch<any>) => ({
  showSnack: (uniqueId: any, args: any) => dispatch(showSnack(uniqueId, args))
});

class WorkflowActionButtons extends React.Component<Props & DispatchProps, State> {
  workflowDeleteLock: boolean;

  constructor(props: Props & DispatchProps) {
    super(props);

    this.state = {
      isConfirmDeleteModalOpen: false
    };

    this.workflowDeleteLock = false;

    this.onDeleteWorkflowClick = this.onDeleteWorkflowClick.bind(this);
    this.toggleDeleteConfirmModal = this.toggleDeleteConfirmModal.bind(this);
    this.deleteWorkflow = this.deleteWorkflow.bind(this);
  }

  onDeleteWorkflowClick() {
    this.toggleDeleteConfirmModal();
  }

  toggleDeleteConfirmModal() {
    this.setState((prevState: State) => ({
      isConfirmDeleteModalOpen: !prevState.isConfirmDeleteModalOpen
    }));
  }

  deleteWorkflow() {
    if (!this.claimDeleteLock()) {
      return;
    }

    ApiRequest.fromPath(endpoints.deleteTemplate(this.props.workflow.code))
      .withAuthHeader()
      .delete()
      .then(response => {
        ResponseHandler.handle(
          response,
          result => {
            // this was successful
            this.releaseDeleteLock();
            this.props.onDelete(this.props.workflow.code);
            this.toggleDeleteConfirmModal();
          },
          result => {
            this.releaseDeleteLock();
            this.props.showSnack("DELETE_WORKFLOW", {
              label: `Failed to delete workflow: ${result.error.message}`,
              timeout: 2000
            });
          },
          () => {
            this.releaseDeleteLock();
            this.props.showSnack("DELETE_WORKFLOW", {
              label: `Failed to delete workflow: An unknown error has occurred (${response.status})`,
              timeout: 2000
            });
          }
        );
      });
  }

  claimDeleteLock(): boolean {
    if (!this.workflowDeleteLock) {
      this.workflowDeleteLock = true;
      return true;
    }
    return false;
  }

  releaseDeleteLock() {
    this.workflowDeleteLock = false;
  }

  render() {
    return (
      <>
        {this.props.workflow.permissions.canEdit && (
          <WorkflowActionButton
            title="Edit Workflow"
            link={appPaths.workflows.edit.replace(":code", this.props.workflow.code)}
            icon={
              <Icon
                appName="GeneralLayout"
                assetName="GENERAL_L_CreateEdit"
                className={iconStyles.smallIcon}
              />
            }
          />
        )}
        {this.props.workflow.permissions.canDelete && (
          <>
            <WorkflowActionButton
              title="Delete Workflow"
              onClick={this.onDeleteWorkflowClick}
              icon={
                <Icon
                  appName="GeneralLayout"
                  assetName="GENERAL_L_DeleteTrash"
                  className={iconStyles.smallIcon}
                />
              }
            />
            <Modal
              isOpen={this.state.isConfirmDeleteModalOpen}
              toggle={this.toggleDeleteConfirmModal}
            >
              <ModalHeader toggle={this.toggleDeleteConfirmModal}>
                Are you sure you want to delete this Workflow
                {this.props.workflow.isTemplate && <span> Template</span>}?
              </ModalHeader>
              <ModalBody>
                {this.props.workflow.isTemplate && (
                  <span>
                    If deleted, <b>{this.props.workflow.name}</b> will no longer be usable as a
                    template for new Topic Workflows
                  </span>
                )}
                {!this.props.workflow.isTemplate && (
                  <span>
                    If deleted, all Feedback currently on the <b>{this.props.workflow.name}</b>{" "}
                    workflow will be reassigned to a default workflow.
                  </span>
                )}
              </ModalBody>
              <ModalFooter>
                <Button color="primary" onClick={this.deleteWorkflow}>
                  Confirm Deletion
                </Button>{" "}
                <Button color="secondary" onClick={this.toggleDeleteConfirmModal}>
                  Cancel
                </Button>
              </ModalFooter>
            </Modal>
          </>
        )}
      </>
    );
  }
}

export default connect(null, mapDispatchToProps)(WorkflowActionButtons);
